<template>
  <div style="width: 70%">
    <b-loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></b-loading>
    <form>
      <b-field horizontal label="Use an intro:">
        <b-switch v-model="useAnIntro"></b-switch>
      </b-field>
      <b-field v-if="useAnIntro" :message="{ 'Please pick an intro': errorIntroText }"
               :type="{ 'is-danger': errorIntroText }"
               horizontal
               label="Intro"
      >
        <b-select v-model="introTextId"
                  @input="option => introTextSelected(option)">
          <option v-for="introText in introTexts" :key="introText.introTextId" :value="introText.introTextId">
            {{ introText.name }} - ({{ introText.cards.length }} cards)
          </option>
        </b-select>
      </b-field>
      <b-field horizontal label="Show other teams:">
        <b-switch v-model="isShowOtherTeamsEnabled"></b-switch>
      </b-field>
      <b-field horizontal label="Players can create teams:">
        <b-switch v-model="playerCanAddTeams"></b-switch>
      </b-field>
      <b-field :message="{ 'In time attack mode, this field is mandatory': errorMaxDuration }"
               :type="{ 'is-danger': errorMaxDuration }"
               horizontal
               label="Time attack (in minutes):">
        <b-numberinput v-model="maxDurationInMinutes" max="240" step="10"></b-numberinput>
      </b-field>

      <hr/>

      <b-collapse :open="false" style="padding-bottom: 40px; margin-top: -10px">
        <a slot="trigger" slot-scope="props">
          <b-icon :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
          <i>{{ !props.open ? 'Show advanced fields' : 'Hide advanced fields' }}</i>
        </a>
        <div style="padding-top: 30px"></div>

        <b-field horizontal label="Start:">
          <b-datepicker v-model="startDate"
                        :first-day-of-week="1"
                        placeholder="Click to select...">

            <button class="button is-primary"
                    @click="startDate = new Date()">
              <b-icon icon="calendar-today"></b-icon>
              <span>Today</span>
            </button>
          </b-datepicker>
          <b-timepicker
              v-model="startTime"
              editable
              icon="clock"
              placeholder="Click to select time"
          >
          </b-timepicker>
        </b-field>

        <b-field horizontal label="End:">
          <b-datepicker v-model="endDate"
                        :first-day-of-week="1"
                        placeholder="Click to select...">

            <button class="button is-primary"
                    @click="endDate = new Date()">
              <b-icon icon="calendar-today"></b-icon>
              <span>Today</span>
            </button>
          </b-datepicker>
          <b-timepicker
              v-model="endTime"
              editable
              icon="clock"
              placeholder="Click to select time"
          >
          </b-timepicker>
        </b-field>

        <b-field horizontal label="Accuracy:">
          <b-slider
              v-model="questionRadius"
              :custom-formatter="val => customFormatterAccuracy(val).val + 'm ' + customFormatterAccuracy(val).text"
              :max="3"
              :min="1"
              :step="1"
              ticks>
            <b-slider-tick :value="1">10m</b-slider-tick>
            <b-slider-tick :value="2">30m</b-slider-tick>
            <b-slider-tick :value="3">80m</b-slider-tick>
          </b-slider>
        </b-field>

        <b-field horizontal label="Finish mode:">
          <b-select v-model="gameOverMode" expanded placeholder="Default: Infinity" rounded
                    @input="gameOverModeSelected">
            <option
                v-for="(mode, index) in gameOverModes"
                :key="index"
                :disabled="disabledFinishedModes(mode)"
                :value="mode">
              {{ mode.name }}
            </option>
          </b-select>
          <p class="control">
            <span class="button is-white"><i>{{ selectedGameOverMode }}</i></span>
          </p>
        </b-field>
        <b-field horizontal label="Disable leaderboard:">
          <b-switch v-model="isLeaderDisabled"></b-switch>
        </b-field>
        <b-field horizontal label="Play with teams:">
          <b-switch v-model="playWithTeams"></b-switch>
        </b-field>
        <b-field horizontal label="Is always on:">
          <b-switch v-model="isAlwaysOn"></b-switch>
        </b-field>
      </b-collapse>
    </form>
  </div>
</template>

<script>
import GAME_OVER_MODES from '../../enums/gameOverModes'
import dateUtils from '../../helpers/date-helper'
import displayHelper from '@/helpers/display-helper'
import introTextsService from '@/services/introTextsService'
import gameStylesHelper from '../../enums/gameStyles'

const noTimezoneFormat = 'YYYY-MM-DDTHH:mm'

var moment = require('moment')

export default {
  props: {
    game: {},
    next: false,
    isEdit: false,
  },
  data() {
    return {
      isLoading: false,
      gameOverModes: [],
      selectedGameOverMode: '',

      gameOverMode: '',
      isLeaderDisabled: true,
      isShowOtherTeamsEnabled: true,
      questionRadius: 0,

      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      playWithTeams: true,
      playerCanAddTeams: false,
      isAlwaysOn: false,
      introTextId: '',
      introTextName: '',
      introTexts: [],
      maxDurationInMinutes: 0,

      useAnIntro: false,
      errorIntroText: false,
      errorMaxDuration: false,
    }
  },
  watch: {
    'next': function (next) {
      if (next) {
        if (!this.validateFields()) {
          console.log('Errors in fields')
          this.$emit('errors')
          return
        }

        // dates are not mandatory in the wizard but still in the old version

        this.startDate = this.startDate ? this.startDate : new Date()
        this.endDate = this.endDate ? this.endDate : new Date()
        this.startTime = this.startTime ? this.startTime : new Date()
        this.endTime = this.endTime ? this.endTime : new Date()

        const step4Game = {
          gameOverMode: this.gameOverMode.value,
          isLeaderDisabled: this.isLeaderDisabled,
          isShowOtherTeamsEnabled: this.isShowOtherTeamsEnabled,
          questionRadius: this.customFormatterAccuracy(this.questionRadius).val,

          gameStartDate: dateUtils.mergeTimeToDate(this.startDate, this.startTime),
          gameEndDate: dateUtils.mergeTimeToDate(this.endDate, this.endTime),

          playWithTeams: this.playWithTeams,
          playerCanAddTeams: this.playerCanAddTeams,
          isAlwaysOn: this.isAlwaysOn,
          introTextId: this.introTextId,
          maxDurationInMinutes: this.maxDurationInMinutes ? this.maxDurationInMinutes : 0,
        }
        if (this.useAnIntro) {
          if (this.introTextId) {
            step4Game.introTextId = this.introTextId
            step4Game.introTextName = this.getNameForIntroTextId(this.introTextId)
          }
        } else {
          step4Game.introTextId = ''
        }

        console.log('Before emit in Next Step 4')
        this.$emit('updated', step4Game)
      }
    }
  },
  created() {
    this.gameOverModes = GAME_OVER_MODES
    this.gameOverMode = GAME_OVER_MODES.INFINITY
    this.selectedGameOverMode = GAME_OVER_MODES.INFINITY.desc

    this.questionRadius = 2
    this.isLeaderDisabled = false
    this.isShowOtherTeamsEnabled = true

    this.playWithTeams = true
    this.playerCanAddTeams = false
    this.isAlwaysOn = false

    this.populateIntros()

    if (this.isEdit) {
      this.gameOverMode = this.game.gameOverMode ? this.game.gameOverMode : GAME_OVER_MODES.INFINITY
      this.questionRadius = this.reverseValueAccuracy(this.game.questionRadius)
      this.isLeaderDisabled = this.game.isLeaderDisabled
      this.isShowOtherTeamsEnabled = this.game.isShowOtherTeamsEnabled

      this.playWithTeams = this.game.playWithTeams
      this.playerCanAddTeams = this.game.playerCanAddTeams
      this.isAlwaysOn = this.game.isAlwaysOn
      this.maxDurationInMinutes = this.game.maxDurationInMinutes ? this.maxDurationInMinutesToNumber(this.game.maxDurationInMinutes) : 0

      if (this.game.introTextId) {
        this.introTextId = this.game.introTextId
        this.useAnIntro = true
      }

      // manque les times et dates
      this.startDate = moment(this.game.gameStartDate, this.noTimezoneFormat).toDate()
      this.startTime = moment(this.game.gameStartDate, this.noTimezoneFormat).toDate()
      this.endDate = moment(this.game.gameEndDate, this.noTimezoneFormat).toDate()
      this.endTime = moment(this.game.gameEndDate, this.noTimezoneFormat).toDate()
    } else {
      if (this.game) {
        this.gameOverMode = this.game.gameOverMode ? this.game.gameOverMode : GAME_OVER_MODES.INFINITY
        this.questionRadius = this.game.questionRadius ? this.reverseValueAccuracy(this.game.questionRadius) : 2
        this.isLeaderDisabled = this.game.isLeaderDisabled ? this.game.isLeaderDisabled : false
        this.isShowOtherTeamsEnabled = this.game.isShowOtherTeamsEnabled ? this.game.isShowOtherTeamsEnabled : true

        this.playWithTeams = this.game.playWithTeams ? this.game.playWithTeams : true
        this.playerCanAddTeams = this.game.playerCanAddTeams ? this.game.playerCanAddTeams : false
        this.isAlwaysOn = this.game.isAlwaysOn ? this.game.isAlwaysOn : false
        this.maxDurationInMinutes = this.game.maxDurationInMinutes ? this.maxDurationInMinutesToNumber(this.game.maxDurationInMinutes) : 0

        this.introTextId = this.game.introTextId ? this.game.introTextId : ''
        this.useAnIntro = !!this.game.introTextId

        // manque les times et dates
        this.startDate = this.game.gameStartDate ? moment(this.game.gameStartDate, this.noTimezoneFormat).toDate() : null
        this.startTime = this.game.gameStartDate ? moment(this.game.gameStartDate, this.noTimezoneFormat).toDate() : null
        this.endDate = this.game.gameEndDate ? moment(this.game.gameEndDate, this.noTimezoneFormat).toDate() : null
        this.endTime = this.game.gameEndDate ? moment(this.game.gameEndDate, this.noTimezoneFormat).toDate() : null
      }
    }
  },
  methods: {
    populateIntros() {
      var parent = this
      this.isLoading = true
      introTextsService.getIntroTexts()
          .then(introTexts => {
            this.introTexts = introTexts
            this.isLoading = false
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops.. An error occurred: ' + err.message)
          })
    },
    getNameForIntroTextId(id) {
      const obj = _.find(this.introTexts, (introText) => {
        return introText.introTextId === id
      })
      return obj ? obj.name : 'Error'
    },
    introTextSelected(option) {
      this.introTextId = option
    },
    disabledFinishedModes(mode) {
      // limit types of game over modes for the moment
      return mode !== GAME_OVER_MODES.INFINITY
    },
    gameOverModeSelected() {
      this.selectedGameOverMode = this.gameOverMode.desc
    },
    customFormatterAccuracy(val) {
      if (val === 1) {
        return {
          val: 10,
          text: '(experimental)'
        }
      }
      if (val === 2) {
        return {
          val: 30,
          text: '(for confined areas)'
        }
      }
      if (val === 3) {
        return {
          val: 80,
          text: '(for wide areas)'
        }
      }
    },
    reverseValueAccuracy(val) {
      if (val === 10) {
        return 1
      } else if (val === 30) {
        return 2
      } else {
        return 3
      }
    },
    maxDurationInMinutesToNumber(value) {
      return /^\d+$/.test(value) ? parseInt(value) : 0
    },
    validateFields() {
      let hasErrors = false

      if (this.useAnIntro && !this.introTextId) {
        this.errorIntroText = true
        return false
      }

      if (this.game && this.game.gameStyle === gameStylesHelper.GAME_STYLES.TIME_ATTACK) {
        console.log('time attack selected: a maxDuration should be entered')
        this.errorMaxDuration = true
        return !!this.maxDurationInMinutes || this.maxDurationInMinutes > 0
      }

      return !hasErrors
    },
  }
}
</script>
<style scoped>

</style>
